import type { OrderedJSONSchema } from "@elephant-healthcare/yupify";
import { Box, Grid, Typography } from "@material-ui/core";
import React from "react";
import DynamicField from "./dynamic-field";
import { DynamicFieldsContext } from "./dynamic-form-fields-context";
import type { FieldMap } from "./types";

export interface DynamicFormFieldsProps {
  parent?: string;
  schema: OrderedJSONSchema;
  fieldMap?: FieldMap;
  lazyFieldMap?: FieldMap;
  useFormValues: () => Record<string, unknown>;
  disabled?: boolean;
  FieldWrapper?: React.ComponentType<WithChildren>;
  fullWidth?: boolean;
}

export interface DynamicObjectInputProps extends DynamicFormFieldsProps {
  name: string;
  fieldDef: OrderedJSONSchema;
  label: string;
}

const DefaultFieldWrapper = ({ children }: WithChildren) => (
  <Grid item xs={12}>
    {children}
  </Grid>
);

/**
 * This component is form-library agnostic
 * You probably want the `RHFDynamicFormFields` wrapper component
 */
export const DynamicObjectInput = ({
  name: parentName,
  fieldDef: parentFieldDef,
  label,
  FieldWrapper,
  ...rest
}: DynamicObjectInputProps): React.ReactElement => {
  return (
    <Box mb={1}>
      {label && <Typography variant="subtitle1">{label}</Typography>}

      <Grid container>
        {parentFieldDef.orderedProperties.map((fieldDef) => {
          const nameWithParent = [parentName, fieldDef.name]
            .filter(Boolean)
            .join(".");

          return (
            <React.Fragment key={nameWithParent}>
              <FieldWrapper>
                <DynamicField
                  {...rest}
                  name={nameWithParent}
                  fieldDef={fieldDef}
                />
              </FieldWrapper>
            </React.Fragment>
          );
        })}
      </Grid>
    </Box>
  );
};

DynamicObjectInput.defaultProps = {
  disabled: false,
  FieldWrapper: DefaultFieldWrapper,
};

export const defaultFieldMap = {
  object: DynamicObjectInput,
};

type FieldMapProps = {
  fieldMap?: FieldMap;
  lazyFieldMap?: FieldMap;
};
const DynamicFormFieldsContainer = (
  props: FieldMapProps & DynamicFormFieldsProps,
) => {
  const { fieldMap, lazyFieldMap, schema, parent, ...restProps } = props;
  const Component = fieldMap.object;
  return (
    <DynamicFieldsContext.Provider value={{ fieldMap, lazyFieldMap }}>
      <Component name={parent ?? ""} fieldDef={schema} {...restProps} />
    </DynamicFieldsContext.Provider>
  );
};

DynamicFormFieldsContainer.defaultProps = {
  fieldMap: defaultFieldMap,
  lazyFieldMap: {},
};

export default React.memo(DynamicFormFieldsContainer);
